import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import {
  MessageSquare, Settings, FolderOpen, FileText, LogOut, Trash2,
  Edit, Save, X, Plus, MessageCircle, Eye, BarChart3, ArrowLeft,
  Check, Leaf, Wind, Archive, Droplets, Zap, HardHat, Upload, ExternalLink,
  Images, Image
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const iconMap = { Leaf, Wind, Archive, Droplets, Zap, HardHat };

const getHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('adminToken')}`
});

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({ messages: 0, unread_messages: 0, services: 0, projects: 0, gallery: 0 });
  const [messages, setMessages] = useState([]);
  const [services, setServices] = useState([]);
  const [projects, setProjects] = useState([]);
  const [content, setContent] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [newPhoto, setNewPhoto] = useState({ title: '', caption: '', image_url: '', category: 'general' });
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoSaved, setPhotoSaved] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [editingProject, setEditingProject] = useState(null);
  const [addingProject, setAddingProject] = useState(false);
  const [contentSaved, setContentSaved] = useState(false);
  const [servicesSaved, setServicesSaved] = useState(null);
  const [newProject, setNewProject] = useState({
    title: { nl: '', en: '', es: '' },
    description: { nl: '', en: '', es: '' },
    location: '', year: new Date().getFullYear(), images: []
  });
  const [newImgUrl, setNewImgUrl] = useState('');

  useEffect(() => {
    document.title = 'Admin Dashboard | Orange Onderhoudsbedrijf';
    const token = localStorage.getItem('adminToken');
    if (!token) { navigate('/admin/login'); return; }
    loadAll();
  }, []);

  const loadAll = async () => {
    try {
      const h = getHeaders();
      const [st, ms, sv, pj, ct, gl] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: h }),
        axios.get(`${API}/messages`, { headers: h }),
        axios.get(`${API}/services`),
        axios.get(`${API}/projects`),
        axios.get(`${API}/content`),
        axios.get(`${API}/gallery`),
      ]);
      setStats(st.data);
      setMessages(ms.data);
      setServices(sv.data);
      setProjects(pj.data);
      setContent(ct.data);
      setPhotos(gl.data);
    } catch (err) {
      if (err.response?.status === 401) { navigate('/admin/login'); }
    }
  };

  const logout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    navigate('/admin/login');
  };

  const markRead = async (id) => {
    await axios.put(`${API}/messages/${id}/read`, {}, { headers: getHeaders() });
    setMessages(prev => prev.map(m => m.id === id ? { ...m, read: true } : m));
    setStats(prev => ({ ...prev, unread_messages: Math.max(0, prev.unread_messages - 1) }));
  };

  const deleteMessage = async (id) => {
    if (!window.confirm('Delete message?')) return;
    await axios.delete(`${API}/messages/${id}`, { headers: getHeaders() });
    setMessages(prev => prev.filter(m => m.id !== id));
    setStats(prev => ({ ...prev, messages: prev.messages - 1 }));
  };

  const saveService = async (service) => {
    await axios.put(`${API}/services/${service.id}`, service, { headers: getHeaders() });
    setServices(prev => prev.map(s => s.id === service.id ? service : s));
    setEditingService(null);
    setServicesSaved(service.id);
    setTimeout(() => setServicesSaved(null), 2000);
  };

  const deleteProject = async (id) => {
    if (!window.confirm('Delete project?')) return;
    await axios.delete(`${API}/projects/${id}`, { headers: getHeaders() });
    setProjects(prev => prev.filter(p => p.id !== id));
    setStats(prev => ({ ...prev, projects: prev.projects - 1 }));
  };

  const createProject = async () => {
    if (!newProject.title.nl || !newProject.location) return;
    const res = await axios.post(`${API}/projects`, newProject, { headers: getHeaders() });
    setProjects(prev => [res.data, ...prev]);
    setStats(prev => ({ ...prev, projects: prev.projects + 1 }));
    setAddingProject(false);
    setNewProject({ title: { nl: '', en: '', es: '' }, description: { nl: '', en: '', es: '' }, location: '', year: new Date().getFullYear(), images: [] });
    setNewImgUrl('');
  };

  const saveContent = async () => {
    await axios.put(`${API}/content`, {
      hero: content.hero,
      about: content.about,
      municipal: content.municipal
    }, { headers: getHeaders() });
    setContentSaved(true);
    setTimeout(() => setContentSaved(false), 2000);
  };

  const updateContentField = (section, field, lang, val) => {
    setContent(prev => ({
      ...prev,
      [section]: { ...prev[section], [field]: { ...prev[section]?.[field], [lang]: val } }
    }));
  };

  const addImageToProject = () => {
    if (!newImgUrl.trim()) return;
    setNewProject(prev => ({ ...prev, images: [...prev.images, newImgUrl.trim()] }));
    setNewImgUrl('');
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { alert('Image is too large (max 2MB).'); return; }
    const reader = new FileReader();
    reader.onload = (ev) => {
      setNewProject(prev => ({ ...prev, images: [...prev.images, ev.target.result] }));
    };
    reader.readAsDataURL(file);
  };

  const addPhoto = async () => {
    if (!newPhoto.image_url.trim()) return;
    setPhotoUploading(true);
    try {
      const res = await axios.post(`${API}/gallery`, newPhoto, { headers: getHeaders() });
      setPhotos(prev => [res.data, ...prev]);
      setStats(prev => ({ ...prev, gallery: (prev.gallery || 0) + 1 }));
      setNewPhoto({ title: '', caption: '', image_url: '', category: 'general' });
      setPhotoSaved(true);
      setTimeout(() => setPhotoSaved(false), 2000);
    } catch (e) { alert('Failed to add photo.'); }
    setPhotoUploading(false);
  };

  const handlePhotoFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) { alert('Image too large (max 3MB).'); return; }
    const reader = new FileReader();
    reader.onload = (ev) => setNewPhoto(prev => ({ ...prev, image_url: ev.target.result }));
    reader.readAsDataURL(file);
  };

  const deletePhoto = async (id) => {
    if (!window.confirm('Delete this photo?')) return;
    await axios.delete(`${API}/gallery/${id}`, { headers: getHeaders() });
    setPhotos(prev => prev.filter(p => p.id !== id));
    setStats(prev => ({ ...prev, gallery: Math.max(0, (prev.gallery || 0) - 1) }));
  };

  const PHOTO_CATEGORIES = [
    { value: 'general', label: 'General' },
    { value: 'grass', label: 'Grass Cutting' },
    { value: 'street', label: 'Street Cleaning' },
    { value: 'garage', label: 'Garage Cleaning' },
    { value: 'window', label: 'Window Cleaning' },
    { value: 'electrical', label: 'Electrical' },
    { value: 'renovation', label: 'Renovation' },
  ];

  const username = localStorage.getItem('adminUser') || 'Admin';

  const statCards = [
    { label: 'Messages', value: stats.messages, icon: MessageSquare, color: 'bg-blue-50 text-blue-600' },
    { label: 'Unread', value: stats.unread_messages, icon: Eye, color: 'bg-orange-50 text-[#FF5F00]' },
    { label: 'Services', value: stats.services, icon: Settings, color: 'bg-green-50 text-green-600' },
    { label: 'Projects', value: stats.projects, icon: FolderOpen, color: 'bg-purple-50 text-purple-600' },
    { label: 'Gallery', value: stats.gallery || 0, icon: Images, color: 'bg-pink-50 text-pink-600' },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header data-testid="admin-header" className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="px-6 md:px-12 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 bg-[#FF5F00] rounded-lg flex items-center justify-center">
                <span className="text-white font-black text-sm">O</span>
              </div>
              <span className="font-black text-[#0F172A] hidden sm:block">Admin <span className="text-[#FF5F00]">Dashboard</span></span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-[#64748B] hidden sm:block">{username}</span>
            <Link to="/" data-testid="admin-view-site" className="flex items-center gap-1 text-sm text-[#64748B] hover:text-[#FF5F00] transition-colors">
              <ExternalLink size={14} /> Site
            </Link>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="flex items-center gap-2 text-sm text-red-500 hover:text-red-700 transition-colors"
            >
              <LogOut size={16} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="px-6 md:px-12 py-8">
        {/* Stats */}
        <div data-testid="admin-stats" className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          {statCards.map((s, i) => (
            <div key={i} className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
              <div className={`w-10 h-10 ${s.color} rounded-lg flex items-center justify-center mb-3`}>
                <s.icon size={20} />
              </div>
              <p className="text-2xl font-black text-[#0F172A]">{s.value}</p>
              <p className="text-xs text-[#64748B] font-medium mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <Tabs defaultValue="messages" className="space-y-6">
          <TabsList className="bg-white border border-slate-200 p-1 rounded-xl">
            <TabsTrigger data-testid="tab-messages" value="messages" className="data-[state=active]:bg-[#FF5F00] data-[state=active]:text-white rounded-lg text-sm font-semibold">
              <MessageSquare size={15} className="mr-1.5" /> Messages
              {stats.unread_messages > 0 && (
                <span className="ml-1.5 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">{stats.unread_messages}</span>
              )}
            </TabsTrigger>
            <TabsTrigger data-testid="tab-services" value="services" className="data-[state=active]:bg-[#FF5F00] data-[state=active]:text-white rounded-lg text-sm font-semibold">
              <Settings size={15} className="mr-1.5" /> Services
            </TabsTrigger>
            <TabsTrigger data-testid="tab-projects" value="projects" className="data-[state=active]:bg-[#FF5F00] data-[state=active]:text-white rounded-lg text-sm font-semibold">
              <FolderOpen size={15} className="mr-1.5" /> Projects
            </TabsTrigger>
            <TabsTrigger data-testid="tab-content" value="content" className="data-[state=active]:bg-[#FF5F00] data-[state=active]:text-white rounded-lg text-sm font-semibold">
              <FileText size={15} className="mr-1.5" /> Content
            </TabsTrigger>
            <TabsTrigger data-testid="tab-gallery" value="gallery" className="data-[state=active]:bg-[#FF5F00] data-[state=active]:text-white rounded-lg text-sm font-semibold">
              <Images size={15} className="mr-1.5" /> Gallery
            </TabsTrigger>
          </TabsList>

          {/* MESSAGES TAB */}
          <TabsContent value="messages">
            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
              <div className="p-5 border-b border-slate-100 flex items-center justify-between">
                <h2 className="font-bold text-[#0F172A]">Contact Messages</h2>
                <span className="text-xs text-[#64748B]">{messages.length} messages</span>
              </div>
              {messages.length === 0 ? (
                <div className="p-12 text-center text-[#94A3B8]">No messages yet.</div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {messages.map(msg => (
                    <div key={msg.id} data-testid={`message-${msg.id}`} className={`p-5 ${!msg.read ? 'bg-orange-50/30' : ''}`}>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            {!msg.read && <div className="w-2 h-2 bg-[#FF5F00] rounded-full flex-shrink-0" />}
                            <span className="font-bold text-[#0F172A] text-sm">{msg.name}</span>
                            <span className="text-xs text-[#94A3B8]">&lt;{msg.email}&gt;</span>
                            <span className="text-xs text-[#94A3B8] ml-auto">{new Date(msg.created_at).toLocaleDateString('nl-NL')}</span>
                          </div>
                          <div className="flex items-center gap-3 mb-1 flex-wrap">
                            {msg.phone && <span className="text-xs text-[#334155]">📱 {msg.phone}</span>}
                            {msg.job_type && <span className="inline-flex items-center text-xs font-semibold bg-orange-50 text-[#FF5F00] border border-orange-100 px-2 py-0.5 rounded-full">🔧 {msg.job_type}</span>}
                          </div>
                          <p className="font-semibold text-[#334155] text-sm mb-1">{msg.subject}</p>
                          <p className="text-[#64748B] text-sm leading-relaxed">{msg.message}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 mt-3">
                        {!msg.read && (
                          <button
                            onClick={() => markRead(msg.id)}
                            data-testid={`mark-read-${msg.id}`}
                            className="flex items-center gap-1 text-xs text-[#64748B] hover:text-[#FF5F00] border border-slate-200 px-3 py-1.5 rounded-lg transition-colors"
                          >
                            <Check size={12} /> Mark Read
                          </button>
                        )}
                        <a
                          href={`https://wa.me/${process.env.REACT_APP_WHATSAPP_NUMBER}?text=Beste%20${encodeURIComponent(msg.name)}%2C%20betreft%3A%20${encodeURIComponent(msg.subject)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`whatsapp-reply-${msg.id}`}
                          className="flex items-center gap-1 text-xs text-green-600 hover:text-green-700 border border-green-200 px-3 py-1.5 rounded-lg transition-colors"
                        >
                          <MessageCircle size={12} /> WhatsApp
                        </a>
                        <a
                          href={`mailto:${msg.email}?subject=Re: ${encodeURIComponent(msg.subject)}`}
                          className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 border border-blue-100 px-3 py-1.5 rounded-lg transition-colors"
                        >
                          E-mail
                        </a>
                        <button
                          onClick={() => deleteMessage(msg.id)}
                          data-testid={`delete-message-${msg.id}`}
                          className="flex items-center gap-1 text-xs text-red-500 hover:text-red-700 border border-red-100 px-3 py-1.5 rounded-lg transition-colors ml-auto"
                        >
                          <Trash2 size={12} /> Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* SERVICES TAB */}
          <TabsContent value="services">
            <div className="space-y-4">
              {services.map(service => {
                const Icon = iconMap[service.icon] || Leaf;
                const isEditing = editingService?.id === service.id;
                return (
                  <div key={service.id} data-testid={`service-edit-${service.id}`} className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
                    <div className="p-5 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-orange-50 rounded-xl flex items-center justify-center flex-shrink-0">
                          <Icon size={22} strokeWidth={1.5} className="text-[#FF5F00]" />
                        </div>
                        <div>
                          <p className="font-bold text-[#0F172A]">{service.name?.nl}</p>
                          <p className="text-xs text-[#94A3B8]">{service.name?.en} · {service.name?.es}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {servicesSaved === service.id && <span className="text-xs text-green-600 flex items-center gap-1"><Check size={12} /> Saved</span>}
                        <button
                          onClick={() => setEditingService(isEditing ? null : { ...service })}
                          data-testid={`edit-service-${service.id}`}
                          className="flex items-center gap-1.5 text-sm px-4 py-2 rounded-lg border border-slate-200 text-[#334155] hover:border-[#FF5F00] hover:text-[#FF5F00] transition-colors"
                        >
                          {isEditing ? <><X size={14} /> Cancel</> : <><Edit size={14} /> Edit</>}
                        </button>
                      </div>
                    </div>
                    {isEditing && (
                      <div className="border-t border-slate-100 p-5 bg-slate-50/50 space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          {['nl', 'en', 'es'].map(l => (
                            <div key={l}>
                              <label className="block text-xs font-bold text-[#64748B] mb-1.5 uppercase">Name ({l.toUpperCase()})</label>
                              <input
                                value={editingService.name?.[l] || ''}
                                onChange={e => setEditingService(prev => ({ ...prev, name: { ...prev.name, [l]: e.target.value } }))}
                                className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-[#0F172A] focus:outline-none focus:border-[#FF5F00] transition-all"
                              />
                            </div>
                          ))}
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          {['nl', 'en', 'es'].map(l => (
                            <div key={l}>
                              <label className="block text-xs font-bold text-[#64748B] mb-1.5 uppercase">Description ({l.toUpperCase()})</label>
                              <textarea
                                value={editingService.description?.[l] || ''}
                                onChange={e => setEditingService(prev => ({ ...prev, description: { ...prev.description, [l]: e.target.value } }))}
                                rows={3}
                                className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-[#0F172A] focus:outline-none focus:border-[#FF5F00] transition-all resize-none"
                              />
                            </div>
                          ))}
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-bold text-[#64748B] mb-1.5 uppercase">Icon Name</label>
                            <select
                              value={editingService.icon || 'Leaf'}
                              onChange={e => setEditingService(prev => ({ ...prev, icon: e.target.value }))}
                              className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-[#0F172A] focus:outline-none focus:border-[#FF5F00] transition-all"
                            >
                              {Object.keys(iconMap).map(k => <option key={k} value={k}>{k}</option>)}
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-bold text-[#64748B] mb-1.5 uppercase">Image URL</label>
                            <input
                              value={editingService.image_url || ''}
                              onChange={e => setEditingService(prev => ({ ...prev, image_url: e.target.value }))}
                              className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-[#0F172A] focus:outline-none focus:border-[#FF5F00] transition-all"
                              placeholder="https://..."
                            />
                          </div>
                        </div>
                        <div className="flex gap-3 pt-2">
                          <button
                            onClick={() => saveService(editingService)}
                            data-testid={`save-service-${service.id}`}
                            className="flex items-center gap-2 bg-[#FF5F00] text-white px-6 py-2.5 rounded-lg text-sm font-bold hover:bg-[#EA580C] transition-all"
                          >
                            <Save size={15} /> Save
                          </button>
                          <button onClick={() => setEditingService(null)} className="px-4 py-2.5 text-sm text-[#64748B] border border-slate-200 rounded-lg hover:bg-slate-50 transition-all">
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </TabsContent>

          {/* PROJECTS TAB */}
          <TabsContent value="projects">
            <div className="space-y-4">
              {/* Add project button */}
              {!addingProject && (
                <button
                  onClick={() => setAddingProject(true)}
                  data-testid="add-project-btn"
                  className="flex items-center gap-2 bg-[#FF5F00] text-white px-6 py-3 rounded-xl font-bold hover:bg-[#EA580C] transition-all shadow-sm"
                >
                  <Plus size={18} /> New Project
                </button>
              )}

              {/* Add project form */}
              {addingProject && (
                <div className="bg-white border-2 border-[#FF5F00]/30 rounded-2xl p-6 space-y-4 shadow-sm">
                  <div className="flex items-center justify-between">
                    <h3 className="font-bold text-[#0F172A]">Add New Project</h3>
                    <button onClick={() => setAddingProject(false)} className="p-1 text-[#94A3B8] hover:text-[#334155]"><X size={18} /></button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {['nl', 'en', 'es'].map(l => (
                      <div key={l}>
                        <label className="block text-xs font-bold text-[#64748B] mb-1.5 uppercase">Title ({l.toUpperCase()})</label>
                        <input
                          value={newProject.title[l]}
                          onChange={e => setNewProject(prev => ({ ...prev, title: { ...prev.title, [l]: e.target.value } }))}
                          className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#FF5F00]"
                          placeholder={`Titel in ${l.toUpperCase()}`}
                        />
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {['nl', 'en', 'es'].map(l => (
                      <div key={l}>
                        <label className="block text-xs font-bold text-[#64748B] mb-1.5 uppercase">Description ({l.toUpperCase()})</label>
                        <textarea
                          value={newProject.description[l]}
                          onChange={e => setNewProject(prev => ({ ...prev, description: { ...prev.description, [l]: e.target.value } }))}
                          rows={2}
                          className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#FF5F00] resize-none"
                        />
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-[#64748B] mb-1.5 uppercase">Location</label>
                      <input
                        value={newProject.location}
                        onChange={e => setNewProject(prev => ({ ...prev, location: e.target.value }))}
                        className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#FF5F00]"
                        placeholder="Amsterdam"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-[#64748B] mb-1.5 uppercase">Year</label>
                      <input
                        type="number"
                        value={newProject.year}
                        onChange={e => setNewProject(prev => ({ ...prev, year: parseInt(e.target.value) || 2024 }))}
                        className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#FF5F00]"
                      />
                    </div>
                  </div>
                  {/* Images */}
                  <div>
                    <label className="block text-xs font-bold text-[#64748B] mb-1.5 uppercase">Images</label>
                    <div className="flex gap-2 mb-2">
                      <input
                        value={newImgUrl}
                        onChange={e => setNewImgUrl(e.target.value)}
                        placeholder="https://... or upload a file"
                        className="flex-1 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#FF5F00]"
                        onKeyDown={e => e.key === 'Enter' && addImageToProject()}
                      />
                      <button onClick={addImageToProject} className="bg-slate-100 hover:bg-slate-200 text-[#334155] px-4 py-2 rounded-lg text-sm font-semibold transition-all">URL</button>
                      <label className="flex items-center gap-1.5 bg-slate-100 hover:bg-slate-200 text-[#334155] px-4 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer">
                        <Upload size={14} /> Upload
                        <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                      </label>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {newProject.images.map((img, i) => (
                        <div key={i} className="relative">
                          <img src={img} alt="" className="w-16 h-16 object-cover rounded-lg border border-slate-200" onError={(e) => { e.target.src = 'https://via.placeholder.com/64'; }} />
                          <button
                            onClick={() => setNewProject(prev => ({ ...prev, images: prev.images.filter((_, idx) => idx !== i) }))}
                            className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs"
                          >×</button>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <button onClick={createProject} data-testid="save-project-btn" className="flex items-center gap-2 bg-[#FF5F00] text-white px-6 py-2.5 rounded-lg text-sm font-bold hover:bg-[#EA580C] transition-all">
                      <Save size={15} /> Save
                    </button>
                    <button onClick={() => setAddingProject(false)} className="px-4 py-2.5 text-sm text-[#64748B] border border-slate-200 rounded-lg hover:bg-slate-50">Cancel</button>
                  </div>
                </div>
              )}

              {/* Project list */}
              {projects.length === 0 && !addingProject && (
                <div className="text-center py-12 text-[#94A3B8]">No projects yet.</div>
              )}
              {projects.map(project => (
                <div key={project.id} data-testid={`project-admin-${project.id}`} className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm flex items-center gap-4">
                  <div className="w-16 h-16 rounded-xl overflow-hidden bg-slate-100 flex-shrink-0">
                    {project.images?.[0] ? (
                      <img src={project.images[0]} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.parentNode.innerHTML = '<div class="w-full h-full flex items-center justify-center text-slate-300 text-xs">?</div>'; }} />
                    ) : <div className="w-full h-full flex items-center justify-center"><FolderOpen size={24} className="text-slate-300" /></div>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-[#0F172A]">{project.title?.nl}</p>
                    <p className="text-xs text-[#94A3B8]">{project.location} · {project.year} · {project.images?.length || 0} photos</p>
                  </div>
                  <button
                    onClick={() => deleteProject(project.id)}
                    data-testid={`delete-project-${project.id}`}
                    className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* CONTENT TAB */}
          <TabsContent value="content">
            {content && (
              <div className="space-y-6">
                {/* Hero */}
                <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
                  <h3 className="font-bold text-[#0F172A] mb-5">Hero Section</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs font-bold text-[#64748B] mb-2 uppercase">Title</label>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        {['nl', 'en', 'es'].map(l => (
                          <input key={l} placeholder={l.toUpperCase()} value={content.hero?.title?.[l] || ''}
                            onChange={e => updateContentField('hero', 'title', l, e.target.value)}
                            className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#FF5F00]" />
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-[#64748B] mb-2 uppercase">Subtitle</label>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        {['nl', 'en', 'es'].map(l => (
                          <textarea key={l} placeholder={l.toUpperCase()} value={content.hero?.subtitle?.[l] || ''}
                            onChange={e => updateContentField('hero', 'subtitle', l, e.target.value)}
                            rows={2} className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#FF5F00] resize-none" />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                {/* About */}
                <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
                  <h3 className="font-bold text-[#0F172A] mb-5">About Us Section</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs font-bold text-[#64748B] mb-2 uppercase">Title</label>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        {['nl', 'en', 'es'].map(l => (
                          <input key={l} placeholder={l.toUpperCase()} value={content.about?.title?.[l] || ''}
                            onChange={e => updateContentField('about', 'title', l, e.target.value)}
                            className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#FF5F00]" />
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-[#64748B] mb-2 uppercase">Text</label>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        {['nl', 'en', 'es'].map(l => (
                          <textarea key={l} placeholder={l.toUpperCase()} value={content.about?.text?.[l] || ''}
                            onChange={e => updateContentField('about', 'text', l, e.target.value)}
                            rows={4} className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#FF5F00] resize-none" />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                {/* Municipal */}
                <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
                  <h3 className="font-bold text-[#0F172A] mb-5">Municipal Services Section</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs font-bold text-[#64748B] mb-2 uppercase">Title</label>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        {['nl', 'en', 'es'].map(l => (
                          <input key={l} placeholder={l.toUpperCase()} value={content.municipal?.title?.[l] || ''}
                            onChange={e => updateContentField('municipal', 'title', l, e.target.value)}
                            className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#FF5F00]" />
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-[#64748B] mb-2 uppercase">Text</label>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        {['nl', 'en', 'es'].map(l => (
                          <textarea key={l} placeholder={l.toUpperCase()} value={content.municipal?.text?.[l] || ''}
                            onChange={e => updateContentField('municipal', 'text', l, e.target.value)}
                            rows={3} className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#FF5F00] resize-none" />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                <button
                  onClick={saveContent}
                  data-testid="save-content-btn"
                  className="flex items-center gap-2 bg-[#FF5F00] text-white px-8 py-3 rounded-xl font-bold hover:bg-[#EA580C] transition-all shadow-sm"
                >
                  {contentSaved ? <><Check size={16} /> Saved!</> : <><Save size={16} /> Save Content</>}
                </button>
              </div>
            )}
          </TabsContent>

          {/* GALLERY TAB */}
          <TabsContent value="gallery">
            <div className="space-y-6">
              {/* Upload form */}
              <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
                <h3 className="font-bold text-[#0F172A] mb-5">Add Photo</h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-[#64748B] mb-1.5 uppercase">Title (optional)</label>
                      <input
                        value={newPhoto.title}
                        onChange={e => setNewPhoto(prev => ({ ...prev, title: e.target.value }))}
                        placeholder="e.g. Park Maintenance Amsterdam"
                        className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#FF5F00]"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-[#64748B] mb-1.5 uppercase">Category</label>
                      <select
                        value={newPhoto.category}
                        onChange={e => setNewPhoto(prev => ({ ...prev, category: e.target.value }))}
                        className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#FF5F00]"
                      >
                        {PHOTO_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-[#64748B] mb-1.5 uppercase">Caption (optional)</label>
                    <input
                      value={newPhoto.caption}
                      onChange={e => setNewPhoto(prev => ({ ...prev, caption: e.target.value }))}
                      placeholder="Short description of the photo"
                      className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#FF5F00]"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-[#64748B] mb-1.5 uppercase">Photo</label>
                    <div className="flex gap-2">
                      <input
                        value={newPhoto.image_url.startsWith('data:') ? '' : newPhoto.image_url}
                        onChange={e => setNewPhoto(prev => ({ ...prev, image_url: e.target.value }))}
                        placeholder="Paste image URL (https://...)"
                        className="flex-1 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#FF5F00]"
                      />
                      <label className="flex items-center gap-1.5 bg-slate-100 hover:bg-[#FF5F00] hover:text-white text-[#334155] px-4 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer">
                        <Upload size={14} /> Upload File
                        <input type="file" accept="image/*" onChange={handlePhotoFileUpload} className="hidden" />
                      </label>
                    </div>
                    {newPhoto.image_url && (
                      <div className="mt-3 relative inline-block">
                        <img
                          src={newPhoto.image_url}
                          alt="Preview"
                          className="h-24 w-auto rounded-lg border border-slate-200 object-cover"
                          onError={e => { e.target.style.display = 'none'; }}
                        />
                        <button
                          onClick={() => setNewPhoto(prev => ({ ...prev, image_url: '' }))}
                          className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center"
                        >×</button>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={addPhoto}
                    disabled={!newPhoto.image_url || photoUploading}
                    data-testid="add-photo-btn"
                    className="flex items-center gap-2 bg-[#FF5F00] text-white px-6 py-2.5 rounded-lg text-sm font-bold hover:bg-[#EA580C] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {photoUploading ? 'Adding...' : photoSaved ? <><Check size={15} /> Added!</> : <><Plus size={15} /> Add Photo</>}
                  </button>
                </div>
              </div>

              {/* Photo grid */}
              <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
                <div className="flex items-center justify-between mb-5">
                  <h3 className="font-bold text-[#0F172A]">Gallery Photos</h3>
                  <span className="text-xs text-[#94A3B8]">{photos.length} photos</span>
                </div>
                {photos.length === 0 ? (
                  <div className="text-center py-16">
                    <div className="w-14 h-14 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-3">
                      <Images size={24} className="text-slate-300" />
                    </div>
                    <p className="text-[#94A3B8] text-sm">No photos yet. Add your first photo above.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                    {photos.map(photo => (
                      <div key={photo.id} data-testid={`gallery-admin-${photo.id}`} className="group relative bg-slate-50 rounded-xl overflow-hidden border border-slate-200 hover:border-orange-200 transition-all">
                        <div className="aspect-square overflow-hidden">
                          <img
                            src={photo.image_url}
                            alt={photo.title || ''}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            onError={e => { e.target.src = 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=200&q=40'; }}
                          />
                        </div>
                        <div className="p-2">
                          {photo.title && <p className="text-xs font-semibold text-[#334155] truncate">{photo.title}</p>}
                          <p className="text-xs text-[#94A3B8] capitalize">{photo.category}</p>
                        </div>
                        <button
                          onClick={() => deletePhoto(photo.id)}
                          data-testid={`delete-photo-${photo.id}`}
                          className="absolute top-1.5 right-1.5 w-7 h-7 bg-red-500/90 hover:bg-red-600 text-white rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
